import { supabase } from './supabase';
import { storagePathFor, type StorageContext } from './storagePaths';

export interface UploadResult {
  publicUrl: string;
  storagePath: string;
  fileSize: number;
}

interface StorageLogPayload {
  user_id: string;
  file_path: string;
  file_size: number;
  action: 'upload' | 'delete';
}

async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      console.warn(`Attempt ${attempt + 1} failed:`, lastError.message);

      if (attempt < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('All retry attempts failed');
}

async function logStorageAction(payload: StorageLogPayload): Promise<void> {
  try {
    await retryWithBackoff(async () => {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-update-data`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          table: 'image_storage_logs',
          operation: 'insert',
          data: payload,
          bypassRLS: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`Storage log failed: ${errorData.error || response.statusText}`);
      }

      return response.json();
    }, 3, 500);
  } catch (error) {
    console.error('Failed to log storage action after retries:', error);

    const queueKey = 'storage_log_queue';
    try {
      const queue = JSON.parse(localStorage.getItem(queueKey) || '[]');
      queue.push({ ...payload, timestamp: Date.now() });
      localStorage.setItem(queueKey, JSON.stringify(queue));
      console.log('Queued storage log for later retry');
    } catch (queueError) {
      console.error('Failed to queue storage log:', queueError);
    }
  }
}

export async function processStorageLogQueue(): Promise<void> {
  const queueKey = 'storage_log_queue';
  try {
    const queue = JSON.parse(localStorage.getItem(queueKey) || '[]');
    if (queue.length === 0) return;

    console.log(`Processing ${queue.length} queued storage logs...`);
    const successful: number[] = [];

    for (let i = 0; i < queue.length; i++) {
      const item = queue[i];
      const { timestamp: _timestamp, ...payload } = item;

      try {
        await logStorageAction(payload);
        successful.push(i);
      } catch (error) {
        console.error('Failed to process queued log:', error);
      }
    }

    if (successful.length > 0) {
      const remaining = queue.filter((_: unknown, idx: number) => !successful.includes(idx));
      localStorage.setItem(queueKey, JSON.stringify(remaining));
      console.log(`Processed ${successful.length} queued logs, ${remaining.length} remaining`);
    }
  } catch (error) {
    console.error('Error processing storage log queue:', error);
  }
}

// De padopbouw staat in storagePaths.ts (zonder supabase-import, dus los te testen).
export type UploadContext = StorageContext;
export const buildStoragePath = storagePathFor;

export async function uploadToStorage(
  blob: Blob,
  userId: string,
  context: UploadContext,
  ids: { sessionId?: string; blogId?: string; postId?: string } = {},
): Promise<UploadResult> {
  const storagePath = storagePathFor(userId, context, ids);

  const arrayBuffer = await blob.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);

  const { error } = await supabase.storage
    .from('blog-images')
    .upload(storagePath, uint8Array, {
      contentType: blob.type,
      upsert: true,
    });

  if (error) {
    console.error('Upload error:', error);
    throw new Error(`Failed to upload image: ${error.message}`);
  }

  const { data: publicUrlData } = supabase.storage
    .from('blog-images')
    .getPublicUrl(storagePath);

  const publicUrl = publicUrlData.publicUrl;
  const fileSize = blob.size;

  await logStorageAction({
    user_id: userId,
    file_path: storagePath,
    file_size: fileSize,
    action: 'upload',
  });

  return {
    publicUrl,
    storagePath,
    fileSize,
  };
}

export async function deleteFromStorage(
  storagePath: string,
  userId: string
): Promise<void> {
  const { data: fileData } = await supabase.storage
    .from('blog-images')
    .list(storagePath.split('/').slice(0, -1).join('/'), {
      search: storagePath.split('/').pop(),
    });

  const fileSize = fileData?.[0]?.metadata?.size || 0;

  const { error } = await supabase.storage
    .from('blog-images')
    .remove([storagePath]);

  if (error) {
    console.error('Delete error:', error);
    throw new Error(`Failed to delete image: ${error.message}`);
  }

  await logStorageAction({
    user_id: userId,
    file_path: storagePath,
    file_size: fileSize,
    action: 'delete',
  });
}